import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { fetchProducts, fetchCategories } from "config/api/inventory.api";
import { Product } from "config/api/inventory.api.types";
import BgSection from "components/common/BgSection";
import ImageCard from "components/common/ImageCard";
import LoadingCard from "components/common/LoadingCard";
import Section from "components/common/Section";
import View from "components/common/View";
import { TabItem } from "components/common/common.types";
import SearchBar from "components/common/SearchBar";
import Banner from "components/common/Banner";
import LoadingPage from "./LoadingPage";
import styles from "./Home.module.scss";
import Popup from "components/common/Popup";
import UnauthorizedPopup from "components/App/UnauthorizedPopup";
import SideMenu from "components/common/SideMenu/SideMenu";

const Home: React.FunctionComponent = () => {
  const [isUserAuthorized, setisUserAuthorized] = useState(false);
  const [categories, setCategories] = useState<TabItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [activeCategory, setActiveCategory] = useState({} as TabItem);
  const [loadingCards, setLoadingCards] = useState(false);
  const navigate = useNavigate();

  let productsCards;

  const {
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    logout,
    getIdTokenClaims,
  } = useAuth0();
  const logUserOut = () => logout({ returnTo: window.location.origin });

  useEffect(() => {
    (async function login() {
      if (!isLoading && !user) {
        await loginWithRedirect();
      }
    })();
  }, [isLoading, user, loginWithRedirect]);

  useEffect(() => {
    let claims = getIdTokenClaims();
    claims.then((userMetadata) => {
      if (userMetadata && userMetadata["deacero_authorized"]) {
        setisUserAuthorized(true);
      }
    });
  }, [getIdTokenClaims, user]);

  useEffect(() => {
    fetchCategories().then((data) => {
      setLoadingCards(true);

      let categories = data.map(
        (category) =>
          ({
            id: category.categoryId,
            title: category.categoryName,
          } as TabItem)
      );
      categories = categories.sort((a, b) => {
        const aTitle = a.title;
        const bTitle = b.title;
        return aTitle < bTitle ? -1 : aTitle > bTitle ? 1 : 0;
      });
      setCategories(categories);
    });
  }, []);

  useEffect(() => {
    if (categories.length) {
      setLoadingCards(true);
    }
    activeCategory?.id &&
      fetchProducts(activeCategory.id).then((data) => {
        const products = data;
        setProducts(products);
        setLoadingCards(false);
      });
  }, [activeCategory, categories]);

  const onSelectCategory = (category: TabItem) => {
    setActiveCategory(category);
  };

  if (loadingCards) {
    productsCards = <LoadingCard cards={8} variant="medium" />;
  } else {
    productsCards = products.map((product) => {
      const productImage = product.images[0]?.url ?? "";
      return (
        <div
          key={product.productId}
          className={styles.pointer}
          onClick={() => navigate(`../producto/${product.productId}`)}
        >
          <ImageCard
            title={product.productName}
            imgSrc={productImage}
            size="medium"
            loading={false}
          />
        </div>
      );
    });
  }

  const homeHeader = (
    <BgSection bgImage="/background.png" position="top">
      <div className={styles["header-content"]}>
        <div className={styles["header-title"]}>
          Conoce el portal que te ayudará a comprar productos DEACERO más fácil
          y rápido.
        </div>
        <SearchBar />
      </div>
    </BgSection>
  );

  if (isAuthenticated && !isLoading) {
    return (
      <View header={homeHeader}>
        {!isUserAuthorized && (
          <Popup
            open={!isUserAuthorized}
            onClose={() => {}}
            content={<UnauthorizedPopup onClose={logUserOut} />}
          />
        )}
        <Banner
          size="large"
          type="info"
          message="Antes de realizar tus compras te recordamos que por motivos de política de la empresa, el mínimo permitido de compra es de $4,000 MXN."
        />
        {categories.length > 0 && (
          <Section title="Catálogo de productos">
            <div className={styles.catalog}>
              <div className={styles["side-menu"]}>
                <SideMenu options={categories} onSelectTab={onSelectCategory} />
              </div>
              <div className={styles["products-container"]}>
                {productsCards}
              </div>
            </div>
          </Section>
        )}
      </View>
    );
  } else {
    return <LoadingPage />;
  }
};

export default Home;
